// Страница вариантов упражнения для преподавателей: CRUD вариантов с маршрутизацией к нужному редактору по типу парсера.

import { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Button,
  Spinner,
  Card,
  Badge,
  Modal,
  Form,
  Alert,
} from 'react-bootstrap';
import {
  ArrowLeft,
  PencilSquare,
  Files,
  Trash,
  Eye,
} from 'react-bootstrap-icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useExerciseStore from '../../store/exerciseStore';
import AppNavbar from '../../components/common/AppNavbar';
import ManagerKeywordTransformVariant from '../../components/manages/ManagerKeywordTransformVariant';
import ManagerLexicsVariant from '../../components/manages/ManagerLexicsVariant';
import ManagerTextFillBankVariant from '../../components/manages/ManagerTextFillBankVariant';
import StudentExercise from '../../components/students/exercises/StudentExercise';
import StudentTextFillBankScreen from '../../components/students/exercises/StudentTextFillBankScreen';
import { getVariant } from '../../api/exerciseApi';

// ── Confirm Delete Modal ───────────────────────────────────────────────────────

function ConfirmDeleteModal({ show, onHide, onConfirm, deleting }) {
  return (
    <Modal show={show} onHide={onHide} size="sm" centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>
          Удалить вариант?
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3">
        <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
          Это действие нельзя отменить.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onHide}>
          Отмена
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={onConfirm}
          disabled={deleting}
          style={{ fontWeight: 500 }}
        >
          {deleting ? <Spinner size="sm" animation="border" /> : 'Удалить'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Replace %1, %2, ... placeholders with visual blank
function formatSentenceText(text) {
  if (!text) return '';
  return text.replace(/%\d+/g, '_____');
}

// Fill %N placeholders with correct answers from answersDict
function fillWithAnswers(sentence, answersDict) {
  if (!sentence || !answersDict) return '';
  return sentence.replace(/%(\d+)/g, (_, pos) => answersDict[pos] ?? '[?]');
}

// ── Variant Card ───────────────────────────────────────────────────────────────

function VariantCard({ variant, index, onEdit, onCopy, onDelete, onPreview, copying }) {
  const sentencesDict = variant.sentences || {};
  const sentenceKeys = Object.keys(sentencesDict).sort((a, b) => Number(a) - Number(b));
  const hasSentences = sentenceKeys.length > 0;

  return (
    <Card
      style={{
        background: '#ffffff',
        border: '1px solid #e8eaf0',
        borderRadius: 'var(--ewl-radius-card)',
        boxShadow: 'var(--ewl-shadow-sm)',
      }}
    >
      <Card.Body className="p-4">
        {/* Top row */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-bold" style={{ fontSize: '0.9375rem', color: '#1e2a4a' }}>
              Вариант {variant.variant ?? index}
            </span>
          </div>
          <div className="d-flex gap-1">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onPreview}
              style={{ borderRadius: '0.4rem', padding: '0.25rem 0.5rem' }}
              title="Просмотр"
            >
              <Eye size={15} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onEdit}
              style={{ borderRadius: '0.4rem', padding: '0.25rem 0.5rem' }}
              title="Редактировать"
            >
              <PencilSquare size={15} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onCopy}
              disabled={copying}
              style={{ borderRadius: '0.4rem', padding: '0.25rem 0.5rem' }}
              title="Дублировать"
            >
              {copying ? <Spinner size="sm" animation="border" /> : <Files size={15} />}
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={onDelete}
              style={{ borderRadius: '0.4rem', padding: '0.25rem 0.5rem' }}
              title="Удалить"
            >
              <Trash size={15} />
            </Button>
          </div>
        </div>

        {/* Sentences list */}
        {hasSentences ? (
          <div className="mb-1">
            {sentenceKeys.map((k, idx) => {
              const s = sentencesDict[k];
              const wordsDict = s.words || {};
              const answersDict = s.answers || {};
              const explanationDict = s.explanation || {};
              const blankKeys = Object.keys(answersDict).sort((a, b) => Number(a) - Number(b));
              return (
                <div
                  key={k}
                  className="py-2"
                  style={idx > 0 ? { borderTop: '1px solid #f0f1f3' } : {}}
                >
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div style={{ flex: 1 }}>
                      <span
                        className="text-muted fst-italic"
                        style={{ fontSize: '0.875rem', lineHeight: '1.6' }}
                      >
                        {k}. {formatSentenceText(s.sentence)}
                      </span>
                      {Object.keys(answersDict).length > 0 && (
                        <div style={{ fontSize: '0.8rem', color: '#198754', marginTop: '0.2rem', fontStyle: 'normal' }}>
                          ✓ {fillWithAnswers(s.sentence, answersDict)}
                        </div>
                      )}
                    </div>
                    <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                      {blankKeys.length > 0 ? blankKeys.map((blankPos) => {
                        const wordsList = wordsDict[blankPos] || [];
                        const answer = answersDict[blankPos];
                        return (
                          <div key={blankPos} className="d-flex align-items-center gap-1">
                            {wordsList.length > 0 && (
                              <Badge
                                bg="light"
                                text="dark"
                                style={{ fontSize: '0.7rem', fontWeight: 600, fontFamily: 'monospace' }}
                              >
                                {wordsList.join(' / ')}
                              </Badge>
                            )}
                            {answer && (
                              <Badge
                                bg="success"
                                style={{ fontSize: '0.75rem', fontWeight: 500 }}
                              >
                                {answer}
                              </Badge>
                            )}
                          </div>
                        );
                      }) : (
                        Object.entries(wordsDict).map(([blankPos, wordsList]) => (
                          <div key={blankPos} className="d-flex align-items-center gap-1">
                            {Array.isArray(wordsList) && wordsList.length > 0 && (
                              <Badge
                                bg="light"
                                text="dark"
                                style={{ fontSize: '0.7rem', fontWeight: 600, fontFamily: 'monospace' }}
                              >
                                {wordsList.join(' / ')}
                              </Badge>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                  {Object.keys(explanationDict).length > 0 && (
                    <div className="mt-1">
                      {Object.entries(explanationDict).map(([blankPos, text]) => (
                        text ? (
                          <div
                            key={blankPos}
                            style={{
                              fontSize: '0.775rem',
                              color: '#6c757d',
                              background: '#f8f9fa',
                              borderRadius: '0.35rem',
                              padding: '0.25rem 0.5rem',
                              marginTop: '0.25rem',
                            }}
                          >
                            {text}
                          </div>
                        ) : null
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Нет предложений
          </p>
        )}
      </Card.Body>
    </Card>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function TeacherVariantsPage() {
  const navigate = useNavigate();
  const { typeId } = useParams();
  const { state } = useLocation();
  const exerciseId = state?.exerciseId;
  const { exercises, types, fetchExercises, fetchTypes, fetchVariants, createVariant, updateVariant, deleteVariant, updateExercise } =
    useExerciseStore();

  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);

  // Variant modal state
  const [modalShow, setModalShow] = useState(false);
  const [editVariant, setEditVariant] = useState(null);

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmShow, setDeleteConfirmShow] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Per-card copy state
  const [copyingId, setCopyingId] = useState(null);

  // Preview state
  const [previewVariant, setPreviewVariant] = useState(null);
  const [previewVariantData, setPreviewVariantData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Exercise edit modal state
  const [exEditShow, setExEditShow] = useState(false);
  const [exEditForm, setExEditForm] = useState({ name: '', description: '', task: '' });
  const [exEditSaving, setExEditSaving] = useState(false);
  const [exEditError, setExEditError] = useState('');

  const exerciseIdNum = Number(exerciseId);

  const currentExercise = exercises.find((ex) => ex.id === exerciseIdNum);
  const currentType     = types.find((t) => t.id === currentExercise?.type_id);
  const exerciseParser  = currentType?.form_type ?? 'keyword_transform';

  const loadVariants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchVariants(exerciseIdNum);
      setVariants(data);
    } catch {
      setVariants([]);
    } finally {
      setLoading(false);
    }
  }, [exerciseIdNum]);

  useEffect(() => {
    const init = async () => {
      if (exercises.length === 0) await fetchExercises();
      if (types.length === 0) await fetchTypes();
      await loadVariants();
    };
    init();
  }, [exerciseIdNum]);

  // ── Create / Edit ────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditVariant(null);
    setModalShow(true);
  };

  const openEdit = (variant) => {
    setEditVariant(variant);
    setModalShow(true);
  };

  const handleSave = async (payload) => {
    let savedVariant;
    if (editVariant) {
      savedVariant = await updateVariant(exerciseIdNum, editVariant.id, payload);
    } else if (payload._variant_id) {
      // Backend already created the variant via parse-text; just refresh the list
    } else {
      savedVariant = await createVariant(exerciseIdNum, payload);
    }
    await loadVariants();
    await fetchExercises();
    return savedVariant;
  };

  // ── Copy ─────────────────────────────────────────────────────────────────────

  const handleCopy = async (variant) => {
    setCopyingId(variant.id);
    try {
      const payload = { variant: null, sentences: variant.sentences || {} };
      await createVariant(exerciseIdNum, payload);
      await loadVariants();
    } catch {
      // silent
    } finally {
      setCopyingId(null);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────

  const openDeleteConfirm = (variant) => {
    setDeleteTarget(variant);
    setDeleteConfirmShow(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteVariant(exerciseIdNum, deleteTarget.id);
      await loadVariants();
      await fetchExercises();
      setDeleteConfirmShow(false);
      setDeleteTarget(null);
    } catch {
      // silent
    } finally {
      setDeleting(false);
    }
  };

  // ── Preview ───────────────────────────────────────────────────────────────────

  const openPreview = async (variant) => {
    setPreviewVariant(variant);
    setPreviewVariantData(null);
    setPreviewLoading(true);
    try {
      const { data } = await getVariant(exerciseIdNum, variant.id);
      setPreviewVariantData({ ...data, form_type: exerciseParser });
    } catch {
      // silent — данные не загружены, покажем ошибку в модалке
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewVariant(null);
    setPreviewVariantData(null);
  };

  // ── Exercise edit ─────────────────────────────────────────────────────────────

  const openExEdit = () => {
    setExEditForm({
      name: currentExercise?.name || '',
      description: currentExercise?.description || '',
      task: currentExercise?.task || '',
    });
    setExEditError('');
    setExEditShow(true);
  };

  const handleExEditSave = async (e) => {
    e.preventDefault();
    if (!exEditForm.name.trim()) { setExEditError('Название обязательно'); return; }
    setExEditSaving(true);
    setExEditError('');
    try {
      await updateExercise(exerciseIdNum, {
        name: exEditForm.name.trim(),
        description: exEditForm.description.trim() || null,
        task: exEditForm.task.trim() || null,
        type_id: currentExercise?.type_id ?? null,
        complexity_level_id: currentExercise?.complexity_level_id ?? 2,
      });
      await fetchExercises();
      setExEditShow(false);
    } catch (err) {
      setExEditError(err?.response?.data?.detail ?? 'Ошибка при сохранении');
    } finally {
      setExEditSaving(false);
    }
  };

  // ── Variant modal by parser type ──────────────────────────────────────────────

  const renderVariantModal = () => {
    const commonProps = {
      show: modalShow,
      onHide: () => setModalShow(false),
      onSave: handleSave,
      editVariant,
      exerciseId: exerciseIdNum,
    };

    if (exerciseParser === 'lexics') {
      return <ManagerLexicsVariant {...commonProps} />;
    }
    if (exerciseParser === 'text_fill_bank') {
      return <ManagerTextFillBankVariant {...commonProps} />;
    }
    return <ManagerKeywordTransformVariant {...commonProps} typeId={Number(typeId)} />;
  };

  return (
    <>
      <AppNavbar />

      <div style={{ background: 'var(--ewl-bg-body)', minHeight: 'calc(100vh - 56px)' }}>
        <Container className="py-4">

          {/* ── Section header ── */}
          <div
            className="d-flex justify-content-between align-items-start mb-4 pb-3"
            style={{ borderBottom: '1px solid #e9ecef' }}
          >
            <div className="d-flex align-items-start gap-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => navigate(`/teacher/exercise-types/${typeId}`)}
                style={{ borderRadius: '0.5rem', flexShrink: 0, marginTop: '0.15rem' }}
              >
                <ArrowLeft size={16} />
              </Button>
              <div>
                <div className="d-flex align-items-center gap-2">
                  <h5 className="fw-bold mb-0" style={{ color: '#1e2a4a' }}>
                    {currentExercise ? currentExercise.name : 'Варианты упражнения'}
                  </h5>
                  {currentExercise && (
                    <Button
                      variant="link"
                      size="sm"
                      onClick={openExEdit}
                      className="p-0"
                      style={{ color: '#adb5bd', lineHeight: 1 }}
                      title="Редактировать упражнение"
                    >
                      <PencilSquare size={15} />
                    </Button>
                  )}
                </div>
                <div className="text-muted mt-1" style={{ fontSize: '0.875rem' }}>
                  Создавайте и редактируйте варианты для данного упражнения
                </div>
              </div>
            </div>
            <Button
              variant="dark"
              onClick={openCreate}
              style={{ borderRadius: '0.5rem', flexShrink: 0 }}
            >
              + Создать вариант
            </Button>
          </div>

          {/* ── Variant count summary ── */}
          {!loading && variants.length > 0 && (
            <div className="mb-3 text-muted" style={{ fontSize: '0.875rem' }}>
              {variants.length}{' '}
              {variants.length === 1
                ? 'вариант'
                : variants.length >= 2 && variants.length <= 4
                ? 'варианта'
                : 'вариантов'}
            </div>
          )}

          {/* ── Content ── */}
          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : variants.length === 0 ? (
            <Card
              className="text-center py-5"
              style={{
                background: '#fff',
                border: '1px solid #e8eaf0',
                borderRadius: 'var(--ewl-radius-card)',
                boxShadow: 'var(--ewl-shadow-sm)',
              }}
            >
              <Card.Body>
                <h6 className="fw-semibold text-muted mb-1">Вариантов пока нет</h6>
                <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                  Создайте первый вариант для этого упражнения
                </p>
                <Button variant="dark" size="sm" onClick={openCreate}>
                  + Создать вариант
                </Button>
              </Card.Body>
            </Card>
          ) : (
            <div className="d-flex flex-column gap-3">
              {variants.map((variant, idx) => (
                <VariantCard
                  key={variant.id}
                  variant={variant}
                  index={idx + 1}
                  onEdit={() => openEdit(variant)}
                  onCopy={() => handleCopy(variant)}
                  onDelete={() => openDeleteConfirm(variant)}
                  onPreview={() => openPreview(variant)}
                  copying={copyingId === variant.id}
                />
              ))}
            </div>
          )}
        </Container>
      </div>

      {/* ── Variant Modal (by parser type) ── */}
      {renderVariantModal()}

      {/* ── Delete Confirm Modal ── */}
      <ConfirmDeleteModal
        show={deleteConfirmShow}
        onHide={() => {
          setDeleteConfirmShow(false);
          setDeleteTarget(null);
        }}
        onConfirm={handleDeleteConfirm}
        deleting={deleting}
      />

      {/* ── Preview Modal ── */}
      <Modal show={previewVariant !== null} onHide={closePreview} fullscreen>
        <Modal.Header closeButton style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #e9ecef' }}>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600, color: '#1e2a4a' }}>
            Просмотр варианта {previewVariant ? `(Вариант ${variants.indexOf(previewVariant) + 1})` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0" style={{ overflow: 'auto', background: 'var(--ewl-bg-body)' }}>
          {previewLoading && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
              <Spinner animation="border" variant="primary" />
            </div>
          )}
          {!previewLoading && !previewVariantData && previewVariant && (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
              <Alert variant="danger" style={{ borderRadius: '0.75rem' }}>
                Не удалось загрузить данные варианта
              </Alert>
            </div>
          )}
          {!previewLoading && previewVariantData && (
            <>
              {exerciseParser === 'text_fill_bank' ? (
                <StudentTextFillBankScreen
                  variant={previewVariantData}
                  historyId={null}
                  exerciseId={exerciseIdNum}
                  variantId={previewVariant?.id}
                  navigate={() => {}}
                  onInterrupt={closePreview}
                  previewMode={true}
                />
              ) : (
                <StudentExercise
                  variant={previewVariantData}
                  historyId={null}
                  exerciseId={exerciseIdNum}
                  variantId={previewVariant?.id}
                  navigate={() => {}}
                  onInterrupt={closePreview}
                  previewMode={true}
                />
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* ── Exercise Edit Modal ── */}
      <Modal show={exEditShow} onHide={() => setExEditShow(false)}>
        <Form onSubmit={handleExEditSave}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600 }}>
              Редактировать упражнение
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {exEditError && (
              <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
                {exEditError}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Название *</Form.Label>
              <Form.Control
                value={exEditForm.name}
                onChange={(e) => setExEditForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Задание</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={exEditForm.task}
                onChange={(e) => setExEditForm((p) => ({ ...p, task: e.target.value }))}
                placeholder="Инструкция для студентов..."
              />
            </Form.Group>
            <Form.Group>
              <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Описание</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={exEditForm.description}
                onChange={(e) => setExEditForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Краткое описание упражнения..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={() => setExEditShow(false)}>
              Отмена
            </Button>
            <Button type="submit" variant="dark" disabled={exEditSaving} style={{ fontWeight: 500 }}>
              {exEditSaving ? <Spinner size="sm" animation="border" /> : 'Сохранить'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
}
